/**
 * Biogas Profile Service
 * Handles API calls for fetching biogas profile data including:
 * - Personal/User Details
 * - Assigned Biogas Clusters
 * - Production & Operational Metrics
 */

const API_BASE_URL = import.meta.env.VITE_BIOGAS_SERVICE_URL || 'http://localhost:8082/biogas-service';

// ============================================================================
// DATA TYPES / INTERFACES
// ============================================================================

/**
 * User/Operator Details
 */
export interface PersonDetails {
  id?: number;
  name?: string;
  phone?: string;
  email?: string;
  locale?: string;
  kycStatus?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  lastLogin?: string;
  primaryRole?: string;
  roles?: string[];
  allowedServices?: string[];
  crossServiceAccess?: boolean;
  aadhaarVerified?: boolean;
  aadhaarVerifiedAt?: string;
}

/**
 * Biogas Cluster Details
 */
export interface BiogasClusterDetails {
  id?: number;
  clusterCode?: string;
  clusterName?: string;
  locationAddress?: string;
  district?: string;
  state?: string;
  pincode?: string;
  latitude?: number;
  longitude?: number;
  totalCapacityM3?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  totalActiveFarmers?: number;
  currentBatchStatus?: string;
  currentBatchProduction?: number;
}

/**
 * Production Metrics
 */
export interface ProductionMetrics {
  totalBatches?: number;
  activeBatches?: number;
  completedBatches?: number;
  totalBiogasProduced30Days?: number;
  avgBatchProduction?: number;
  productionEfficiencyPercent?: number;
}

/**
 * Quality Metrics
 */
export interface QualityMetrics {
  avgMethaneContent?: number;
  avgH2sContent?: number;
  premiumGradePercent?: number;
  standardGradePercent?: number;
  basicGradePercent?: number;
}

/**
 * Collection Analytics
 */
export interface CollectionMetrics {
  totalDungCollected30Days?: number;
  activeContributors?: number;
  avgCollectionPerFarmer?: number;
  gradeAPercent?: number;
  gradeBPercent?: number;
  gradeCPercent?: number;
  gradeDPercent?: number;
}

/**
 * Financial Summary
 */
export interface FinancialMetrics {
  totalPayments30Days?: number;
  avgPaymentPerFarmer?: number;
  pendingPayments?: number;
  failedPayments?: number;
  paymentSuccessRate?: number;
}

/**
 * Subsidy & Carbon Credits
 */
export interface SubsidyMetrics {
  farmersEligibleForSubsidy?: number;
  pendingSubsidyClaims?: number;
  approvedSubsidyAmount?: number;
  carbonCreditsGeneratedYTD?: number;
  carbonCreditsPendingMonetization?: number;
}

/**
 * Operational Health
 */
export interface HealthMetrics {
  systemUptime?: number;
  activeAlerts?: number;
  lastMaintenance?: string;
  nextMaintenance?: string;
}

/**
 * Complete Summary
 */
export interface SummaryMetrics {
  production?: ProductionMetrics;
  quality?: QualityMetrics;
  collection?: CollectionMetrics;
  financial?: FinancialMetrics;
  subsidy?: SubsidyMetrics;
  health?: HealthMetrics;
}

/**
 * Complete Biogas Profile Response
 */
export interface BiogasProfileResponse {
  personDetails?: PersonDetails;
  clusters?: BiogasClusterDetails[];
  summary?: SummaryMetrics;
}

/**
 * API Response Wrapper
 */
export interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// ============================================================================
// API METHODS
// ============================================================================

/**
 * Get complete biogas profile including personal details, clusters, and summary
 */
export const getBiogasProfile = async (): Promise<BiogasProfileResponse> => {
  try {
    const token = getAuthToken();
    console.log('🔑 Auth Token:', token ? `${token.substring(0, 20)}...` : 'MISSING');
    console.log('🌐 API URL:', `${API_BASE_URL}/api/v1/profile`);

    const response = await fetch(`${API_BASE_URL}/api/v1/profile`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    console.log('📨 Response Status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ API Error Response:', errorData);
      throw new Error(
        errorData?.message || `API Error: ${response.status} ${response.statusText}`
      );
    }

    const result = await response.json();
    console.log('📦 Raw Response:', result);

    const profileData = result.data || result;
    console.log('✅ Final Profile Data:', profileData);

    return profileData;
  } catch (error) {
    console.error('❌ Error fetching biogas profile:', error);
    throw error;
  }
};

/**
 * Get personal details only
 */
export const getPersonDetails = async (): Promise<PersonDetails> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/profile/personal`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch personal details: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching personal details:', error);
    throw error;
  }
};

/**
 * Get all assigned biogas clusters
 */
export const getBiogasClusters = async (): Promise<BiogasClusterDetails[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/profile/clusters`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch clusters: ${response.statusText}`);
    }

    const result = await response.json();
    return (result.data || result) as BiogasClusterDetails[];
  } catch (error) {
    console.error('Error fetching clusters:', error);
    throw error;
  }
};

/**
 * Get cluster-specific metrics/summary
 */
export const getClusterSummary = async (clusterId: number): Promise<SummaryMetrics> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/profile/clusters/${clusterId}/summary`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cluster summary: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching cluster summary:', error);
    throw error;
  }
};

/**
 * Get overall summary metrics
 */
export const getSummaryMetrics = async (): Promise<SummaryMetrics> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/profile/summary`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch summary metrics: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching summary metrics:', error);
    throw error;
  }
};

/**
 * Update personal details
 */
export const updatePersonDetails = async (details: Partial<PersonDetails>): Promise<PersonDetails> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/profile/personal`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify(details),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.message || `Failed to update personal details: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error updating personal details:', error);
    throw error;
  }
};

/**
 * Get profile picture/avatar URL for user
 */
export const getProfilePictureUrl = (userId?: number | string): string => {
  if (!userId) return '';
  return `${API_BASE_URL}/api/v1/profile/avatar/${userId}`;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get authentication token from localStorage
 */
const getAuthToken = (): string => {
  return localStorage.getItem('saubhagya_jwt_token') || '';
};

/**
 * Format currency values
 */
export const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null) return '₹0';
  return `₹${value.toLocaleString('en-IN')}`;
};

/**
 * Format large numbers
 */
export const formatNumber = (value?: number): string => {
  if (value === undefined || value === null) return '0';
  return value.toLocaleString('en-IN');
};

/**
 * Format decimal values with 2 decimal places
 */
export const formatDecimal = (value?: number): string => {
  if (value === undefined || value === null) return '0.00';
  return value.toFixed(2);
};

/**
 * Format percentage values
 */
export const formatPercent = (value?: number): string => {
  if (value === undefined || value === null) return '0%';
  return `${value.toFixed(2)}%`;
};

/**
 * Format date string
 */
export const formatDate = (dateString?: string): string => {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

/**
 * Format datetime string with time
 */
export const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};
